import React, { useEffect, useRef, useState } from 'react';
import { Lounge } from '../data/lounges';
import { Loader2, MapPin } from 'lucide-react';

interface GoogleMapProps {
  lounges: Lounge[];
  userLocation: { lat: number; lng: number } | null;
  onSelectLounge: (lounge: Lounge) => void;
  selectedLoungeId?: string;
}

// Declare google maps types
declare global {
  interface Window {
    google: typeof google;
    initGoogleMap: () => void;
  }
}

const GoogleMap: React.FC<GoogleMapProps> = ({
  lounges,
  userLocation,
  onSelectLounge,
  selectedLoungeId,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  // Fetch the API key from our backend
  useEffect(() => {
    const fetchApiKey = async () => {
      try {
        const response = await fetch('/api/lounges/map-key');
        const data = await response.json() as { data?: { key?: string } };
        if (data.data?.key) {
          setApiKey(data.data.key);
        } else {
          setError('Map not available');
        }
      } catch {
        setError('Failed to load map');
      }
    };
    fetchApiKey();
  }, []);

  // Load Google Maps script
  useEffect(() => {
    if (!apiKey) return;

    // Check if already loaded
    if (window.google?.maps) {
      initializeMap();
      return;
    }

    // Create callback
    window.initGoogleMap = () => {
      initializeMap();
    };

    // Load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initGoogleMap`;
    script.async = true;
    script.defer = true;
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);

    return () => {
      // Cleanup
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [apiKey]);

  const initializeMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const center = userLocation || { lat: 26.4317, lng: -81.7962 }; // Default to Naples, FL

    mapInstanceRef.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: userLocation ? 11 : 9,
      styles: darkMapStyle,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    infoWindowRef.current = new google.maps.InfoWindow();

    setIsLoading(false);
    updateMarkers();
  };

  // Update markers when lounges change
  useEffect(() => {
    if (mapInstanceRef.current) {
      updateMarkers();
    }
  }, [lounges, selectedLoungeId]);

  // Update user location marker
  useEffect(() => {
    if (!mapInstanceRef.current || !userLocation) return;

    // Remove old user marker
    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
    }

    // Add new user marker
    userMarkerRef.current = new google.maps.Marker({
      position: userLocation,
      map: mapInstanceRef.current,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#3B82F6',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2,
      },
      title: 'Your Location',
      zIndex: 1000,
    });

    // Center map on user location
    mapInstanceRef.current.setCenter(userLocation);
    mapInstanceRef.current.setZoom(11);
  }, [userLocation]);

  const updateMarkers = () => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add lounge markers
    lounges.forEach(lounge => {
      const isSelected = lounge.id === selectedLoungeId;

      const marker = new google.maps.Marker({
        position: lounge.location,
        map: mapInstanceRef.current!,
        icon: {
          url: `data:image/svg+xml,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="14" fill="${isSelected ? '#F59E0B' : lounge.featured ? '#F59E0B' : '#FFFFFF'}" stroke="${isSelected ? '#78350F' : '#1C1917'}" stroke-width="2"/>
              <text x="16" y="21" text-anchor="middle" font-size="14">${lounge.source === 'google_places' ? '📍' : '🚬'}</text>
            </svg>
          `)}`,
          scaledSize: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16),
        },
        title: lounge.name,
        zIndex: isSelected ? 100 : lounge.featured ? 50 : 10,
      });

      marker.addListener('click', () => {
        // Show info window
        if (infoWindowRef.current) {
          infoWindowRef.current.setContent(`
            <div style="padding: 8px; max-width: 200px;">
              <strong style="font-size: 14px;">${lounge.name}</strong>
              <p style="margin: 4px 0; font-size: 12px; color: #666;">${lounge.city}, ${lounge.state}</p>
              ${lounge.rating ? `<p style="margin: 4px 0; font-size: 12px;">⭐ ${lounge.rating} (${lounge.reviewCount || 0} reviews)</p>` : ''}
              <button onclick="window.selectLounge('${lounge.id}')" style="
                margin-top: 8px;
                padding: 6px 12px;
                background: #F59E0B;
                color: #1C1917;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 12px;
                font-weight: 500;
              ">View Details</button>
            </div>
          `);
          infoWindowRef.current.open(mapInstanceRef.current!, marker);
        }
      });

      markersRef.current.push(marker);
    });

    // Set up global function for info window button
    (window as Window & { selectLounge?: (id: string) => void }).selectLounge = (loungeId: string) => {
      const lounge = lounges.find(l => l.id === loungeId);
      if (lounge) {
        onSelectLounge(lounge);
      }
    };

    // Fit bounds to show all markers if we have lounges
    if (lounges.length > 0 && !userLocation) {
      const bounds = new google.maps.LatLngBounds();
      lounges.forEach(lounge => bounds.extend(lounge.location));
      mapInstanceRef.current.fitBounds(bounds, 50);
    }
  };

  if (error) {
    return (
      <div className="relative h-48 bg-gradient-to-br from-stone-800 to-stone-900 flex items-center justify-center">
        <div className="text-center text-stone-500">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-48">
      {isLoading && (
        <div className="absolute inset-0 bg-stone-800 flex items-center justify-center z-10">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
    </div>
  );
};

// Dark map style for the cigar theme
const darkMapStyle: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#263c3f' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b9a76' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#38414e' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#212a37' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca5b3' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#746855' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#1f2835' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#f3d19c' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#2f3948' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#d59563' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#17263c' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#515c6d' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#17263c' }],
  },
];

export default GoogleMap;
